/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type ChangeEvent, type FC, useId } from "react";
import { useTranslation } from "react-i18next";
import {
  Heading,
  InlineField,
  Label,
  RadioControl,
  Root as Form,
  Separator,
  Text,
} from "@vector-im/compound-web";

import { FieldRow, InputField } from "../input/Input";
import {
  showReactions as showReactionsSetting,
  playReactionsSound as playReactionsSoundSetting,
  developerMode as developerModeSetting,
  joinWithMicrophoneMuted as joinWithMicrophoneMutedSetting,
  joinWithCameraMuted as joinWithCameraMutedSetting,
  callTransportMode as callTransportModeSetting,
  useSetting,
} from "./settings";

export const PreferencesSettingsTab: FC = () => {
  const { t } = useTranslation();
  const [showReactions, setShowReactions] = useSetting(showReactionsSetting);

  const [playReactionsSound, setPlayReactionSound] = useSetting(
    playReactionsSoundSetting,
  );
  const [joinWithMicrophoneMuted, setJoinWithMicrophoneMuted] = useSetting(
    joinWithMicrophoneMutedSetting,
  );
  const [joinWithCameraMuted, setJoinWithCameraMuted] = useSetting(
    joinWithCameraMutedSetting,
  );

  const onChangeSetting = (
    e: ChangeEvent<HTMLInputElement>,
    fn: (value: boolean) => void,
  ): void => {
    fn(e.target.checked);
  };

  const [developerMode, setDeveloperMode] = useSetting(developerModeSetting);
  const [transportMode, setTransportMode] = useSetting(
    callTransportModeSetting,
  );
  const transportGroupId = useId();

  return (
    <div>
      <Text>{t("settings.preferences_tab.introduction")}</Text>
      <FieldRow>
        <InputField
          id="showReactions"
          label={t("settings.preferences_tab.reactions_show_label")}
          description={t("settings.preferences_tab.reactions_show_description")}
          type="checkbox"
          checked={showReactions}
          onChange={(e) => onChangeSetting(e, setShowReactions)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="playReactionSound"
          label={t("settings.preferences_tab.reactions_play_sound_label")}
          description={t(
            "settings.preferences_tab.reactions_play_sound_description",
          )}
          type="checkbox"
          checked={playReactionsSound}
          onChange={(e) => onChangeSetting(e, setPlayReactionSound)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="joinWithMicrophoneMuted"
          label={t("settings.preferences_tab.join_with_microphone_muted_label")}
          description={t(
            "settings.preferences_tab.join_with_microphone_muted_description",
          )}
          type="checkbox"
          checked={joinWithMicrophoneMuted}
          onChange={(e) => onChangeSetting(e, setJoinWithMicrophoneMuted)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="joinWithCameraMuted"
          label={t("settings.preferences_tab.join_with_camera_muted_label")}
          description={t(
            "settings.preferences_tab.join_with_camera_muted_description",
          )}
          type="checkbox"
          checked={joinWithCameraMuted}
          onChange={(e) => onChangeSetting(e, setJoinWithCameraMuted)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="developerSettingsTab"
          type="checkbox"
          checked={developerMode}
          label={t("settings.preferences_tab.developer_mode_label")}
          description={t(
            "settings.preferences_tab.developer_mode_label_description",
          )}
          onChange={(event: ChangeEvent<HTMLInputElement>): void =>
            setDeveloperMode(event.target.checked)
          }
        />
      </FieldRow>
      <Separator />
      <Heading type="body" weight="semibold" size="sm" as="h4">
        {t("settings.preferences_tab.transport_label")}
      </Heading>
      <Text size="sm">
        {t("settings.preferences_tab.transport_description")}
      </Text>
      <Form>
        <InlineField
          name={transportGroupId}
          control={
            <RadioControl
              checked={transportMode === "auto"}
              value="auto"
              onChange={(): void => setTransportMode("auto")}
            />
          }
        >
          <Label>{t("settings.preferences_tab.transport_auto_label")}</Label>
          <Text size="sm">
            {t("settings.preferences_tab.transport_auto_description")}
          </Text>
        </InlineField>
        <InlineField
          name={transportGroupId}
          control={
            <RadioControl
              checked={transportMode === "tcp"}
              value="tcp"
              onChange={(): void => setTransportMode("tcp")}
            />
          }
        >
          <Label>{t("settings.preferences_tab.transport_tcp_label")}</Label>
          <Text size="sm">
            {t("settings.preferences_tab.transport_tcp_description")}
          </Text>
        </InlineField>
        <InlineField
          name={transportGroupId}
          control={
            <RadioControl
              checked={transportMode === "udp"}
              value="udp"
              onChange={(): void => setTransportMode("udp")}
            />
          }
        >
          <Label>{t("settings.preferences_tab.transport_udp_label")}</Label>
          <Text size="sm">
            {t("settings.preferences_tab.transport_udp_description")}
          </Text>
        </InlineField>
      </Form>
    </div>
  );
};
